/**
 * CBD Dosage Calculator - Logic & Interactivity
 * Ported from React/TypeScript to Vanilla JS Web Component.
 */

// ─── Constants & Logic ───────────────────────────────────────

const INTENSITY_MULTIPLIERS = {
  low: 0.1,
  standard: 0.2,
  higher: 0.3,
};

const LBS_PER_KG = 2.20462;
const DEFAULT_DROPPER_SIZE_ML = 1;
const DEFAULT_DROPS_PER_ML = 20;

function toLbs(weight, unit) {
  return unit === "kg" ? weight * LBS_PER_KG : weight;
}

function roundToHalf(value) {
  return Math.round(value * 2) / 2;
}

function formatDropperFraction(value) {
  if (value <= 0) return "less than 1/4 dropper";

  const whole = Math.floor(value);
  const remainder = value - whole;

  const fractionMap = {
    0.25: "1/4",
    0.5: "1/2",
    0.75: "3/4",
  };

  const fractionStr = fractionMap[remainder] ?? "";

  if (whole === 0 && fractionStr) {
    return `${fractionStr} dropper`;
  }
  if (whole > 0 && fractionStr) {
    return `${whole} and ${fractionStr} droppers`;
  }
  return `${whole} full ${whole === 1 ? "dropper" : "droppers"}`;
}

function toDropperFraction(mlPerDay, dropperSizeMl = DEFAULT_DROPPER_SIZE_ML) {
  const rawFraction = mlPerDay / dropperSizeMl;
  const snapped = Math.round(rawFraction * 4) / 4;
  const value = Math.max(0.25, snapped);
  return { label: formatDropperFraction(value), value };
}

function calculateDosage(weight, unit, intensity) {
  const weightInLbs = toLbs(weight, unit);
  const targetMgPerDay = weightInLbs * INTENSITY_MULTIPLIERS[intensity];
  return {
    targetMgPerDay: Math.round(targetMgPerDay * 10) / 10,
    weightInLbs: Math.round(weightInLbs * 10) / 10,
  };
}

function calculateOilServings(
  targetMgPerDay,
  bottleSizeMl,
  bottleStrengthMg,
  dropsPerMl = null,
  dropperSizeMl = DEFAULT_DROPPER_SIZE_ML
) {
  const mgPerMl = bottleStrengthMg / bottleSizeMl;
  const mlPerDayRaw = targetMgPerDay / mgPerMl;
  const mlPerDay = roundToHalf(mlPerDayRaw);
  const dropperFraction = toDropperFraction(mlPerDay, dropperSizeMl);
  const dropsPerDay =
    dropsPerMl !== null && dropsPerMl > 0
      ? Math.round(mlPerDay * dropsPerMl)
      : null;

  return {
    mgPerMl: Math.round(mgPerMl * 10) / 10,
    mlPerDay,
    dropperFraction,
    dropsPerDay,
  };
}

function calculateUnitServings(targetMgPerDay, mgPerUnit) {
  const unitsPerDayRaw = targetMgPerDay / mgPerUnit;
  const unitsPerDay = Math.max(1, Math.round(unitsPerDayRaw));

  return {
    unitsPerDay,
    mgPerUnit,
  };
}

// ─── Web Component ───────────────────────────────────────────

class CBDCalculator extends HTMLElement {
  constructor() {
    super();
    this.style.display = "block";
    this.state = {
      weight: "",
      unit: "lbs",
      intensity: "standard",
      productType: null, // 'oil', 'capsule', 'gummy'
      bottleSizeMl: "",
      bottleStrengthMg: "",
      dropsPerMl: String(DEFAULT_DROPS_PER_ML),
      showDrops: false,
      mgPerUnit: "",
      showResults: false,
    };
  }

  connectedCallback() {
    this.cacheElements();
    this.initEventListeners();
    this.render();
  }

  cacheElements() {
    // Inputs
    this.weightInput = this.querySelector("#weight-input");
    this.unitBtns = this.querySelectorAll(".weight__toggle-btn");
    this.intensityBtns = this.querySelectorAll(".intensity__option");
    this.productBtns = this.querySelectorAll(".product-type__option");
    this.clearProductBtn = this.querySelector(".product-type__clear");
    
    // Strength Panels
    this.oilPanel = this.querySelector('[data-panel="oil"]');
    this.unitPanel = this.querySelector('[data-panel="unit"]');
    this.unitLabelElements = this.querySelectorAll("[data-unit-label]");
    
    // Strength Inputs
    this.bottleStrengthInput = this.querySelector("#bottle-strength");
    this.bottleSizeInput = this.querySelector("#bottle-size");
    this.mgPerUnitInput = this.querySelector("#mg-per-unit");
    this.dropsPerMlInput = this.querySelector("#drops-per-ml");
    
    // Advanced Toggle
    this.advancedToggle = this.querySelector(".strength__advanced-toggle");
    this.advancedBody = this.querySelector(".strength__advanced-body");
    this.advancedIcon = this.querySelector(".strength__advanced-icon");

    // Actions
    this.calculateBtn = this.querySelector(".calc__btn");
    this.resetBtn = this.querySelector(".calc__reset");
    
    // Results
    this.resultsContainer = this.querySelector(".calc__results");
  }

  initEventListeners() {
    // Weight
    this.weightInput?.addEventListener("input", (e) => {
      this.updateState({ weight: e.target.value, showResults: false });
    });

    // Unit Toggle
    this.unitBtns.forEach((btn) => {
      btn.addEventListener("click", () => {
        const unit = btn.getAttribute("data-unit");
        this.updateState({ unit, showResults: false });
      });
    });

    // Intensity
    this.intensityBtns.forEach((btn) => {
      btn.addEventListener("click", () => {
        const intensity = btn.getAttribute("data-intensity");
        this.updateState({ intensity, showResults: false });
      });
    });

    // Product Type
    this.productBtns.forEach((btn) => {
      btn.addEventListener("click", () => {
        const type = btn.getAttribute("data-type");
        const newType = this.state.productType === type ? null : type;
        this.updateState({ productType: newType, showResults: false });
      });
    });

    this.clearProductBtn?.addEventListener("click", () => {
      this.updateState({ productType: null, showResults: false });
    });

    // Strength Inputs
    this.bottleStrengthInput?.addEventListener("input", (e) => {
      this.updateState({ bottleStrengthMg: e.target.value, showResults: false });
    });
    this.bottleSizeInput?.addEventListener("input", (e) => {
      this.updateState({ bottleSizeMl: e.target.value, showResults: false });
    });
    this.mgPerUnitInput?.addEventListener("input", (e) => {
      this.updateState({ mgPerUnit: e.target.value, showResults: false });
    });
    this.dropsPerMlInput?.addEventListener("input", (e) => {
      this.updateState({ dropsPerMl: e.target.value, showResults: false });
    });

    // Advanced Toggle
    this.advancedToggle?.addEventListener("click", () => {
      this.updateState({ showDrops: !this.state.showDrops });
    });

    // Actions
    this.calculateBtn?.addEventListener("click", () => this.handleCalculate());
    this.resetBtn?.addEventListener("click", () => this.handleReset());
  }

  updateState(newState) {
    this.state = { ...this.state, ...newState };
    this.render();
  }

  handleCalculate() {
    const weightNum = parseFloat(this.state.weight);
    if (isNaN(weightNum) || weightNum <= 0) return;

    this.updateState({ showResults: true });
    this.renderResults();
    
    // Scroll to results on mobile
    if (window.innerWidth < 768 && this.resultsContainer) {
      this.resultsContainer.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }

  handleReset() {
    this.state = {
      weight: "",
      unit: "lbs",
      intensity: "standard",
      productType: null,
      bottleSizeMl: "",
      bottleStrengthMg: "",
      dropsPerMl: String(DEFAULT_DROPS_PER_ML),
      showDrops: false,
      mgPerUnit: "",
      showResults: false,
    };
    
    // Clear actual input values
    if (this.weightInput) this.weightInput.value = "";
    if (this.bottleStrengthInput) this.bottleStrengthInput.value = "";
    if (this.bottleSizeInput) this.bottleSizeInput.value = "";
    if (this.mgPerUnitInput) this.mgPerUnitInput.value = "";
    if (this.dropsPerMlInput) this.dropsPerMlInput.value = String(DEFAULT_DROPS_PER_ML);

    this.render();
  }

  render() {
    const { weight, unit, intensity, productType, showDrops, showResults } = this.state;

    // 1. Weight UI
    if (this.weightInput) {
      this.weightInput.placeholder = unit === "lbs" ? "e.g. 150" : "e.g. 68";
    }
    const weightSuffix = this.querySelector(".weight__suffix");
    if (weightSuffix) weightSuffix.textContent = unit;

    this.unitBtns.forEach((btn) => {
      const isActive = btn.getAttribute("data-unit") === unit;
      btn.classList.toggle("weight__toggle-btn--active", isActive);
      btn.setAttribute("aria-checked", isActive);
    });

    // 2. Intensity UI
    this.intensityBtns.forEach((btn) => {
      const isSelected = btn.getAttribute("data-intensity") === intensity;
      btn.classList.toggle("intensity__option--selected", isSelected);
      btn.setAttribute("aria-checked", isSelected);
    });

    // 3. Product Type UI
    this.productBtns.forEach((btn) => {
      const isSelected = btn.getAttribute("data-type") === productType;
      btn.classList.toggle("product-type__option--selected", isSelected);
      btn.setAttribute("aria-checked", isSelected);
    });
    this.clearProductBtn?.classList.toggle("hidden", !productType);

    // 4. Strength Panels Visibility
    this.oilPanel?.classList.toggle("hidden", productType !== "oil");
    this.unitPanel?.classList.toggle("hidden", productType !== "capsule" && productType !== "gummy");
    
    if (productType === "capsule" || productType === "gummy") {
      const label = productType === "capsule" ? "capsule" : "gummy";
      this.unitLabelElements.forEach(el => el.textContent = label);
      if (this.mgPerUnitInput) {
        this.mgPerUnitInput.placeholder = "e.g. 25";
      }
    }

    // 5. Advanced Drops UI
    this.advancedBody?.classList.toggle("hidden", !showDrops);
    this.advancedIcon?.classList.toggle("strength__advanced-icon--open", showDrops);
    this.advancedToggle?.setAttribute("aria-expanded", showDrops);

    // 6. Actions UI
    const weightNum = parseFloat(weight);
    const isWeightValid = !isNaN(weightNum) && weightNum > 0;
    if (this.calculateBtn) {
      this.calculateBtn.classList.toggle("calc__btn--disabled", !isWeightValid);
      this.calculateBtn.disabled = !isWeightValid;
    }
    this.resetBtn?.classList.toggle("hidden", !showResults);

    // 7. Results Container Visibility
    this.resultsContainer?.classList.toggle("hidden", !showResults);
  }

  renderResults() {
    const { weight, unit, intensity, productType, bottleSizeMl, bottleStrengthMg, dropsPerMl, showDrops, mgPerUnit } = this.state;
    
    const weightNum = parseFloat(weight);
    const dosage = calculateDosage(weightNum, unit, intensity);

    // Update Primary Result
    const mgResult = this.querySelector("[data-result-mg]");
    if (mgResult) mgResult.textContent = `${dosage.targetMgPerDay} mg`;

    // Update Oil Results
    const oilResultArea = this.querySelector("[data-result-oil-area]");
    if (productType === "oil" && oilResultArea) {
      const sizeMl = parseFloat(bottleSizeMl);
      const strengthMg = parseFloat(bottleStrengthMg);
      
      if (!isNaN(sizeMl) && sizeMl > 0 && !isNaN(strengthMg) && strengthMg > 0) {
        const drops = showDrops ? parseFloat(dropsPerMl) : null;
        const oilResult = calculateOilServings(dosage.targetMgPerDay, sizeMl, strengthMg, drops);
        
        oilResultArea.classList.remove("hidden");
        
        const oilLabel = this.querySelector("[data-result-oil-label]");
        if (oilLabel) oilLabel.textContent = oilResult.dropperFraction.label;
        
        const oilMlNote = this.querySelector("[data-result-oil-ml-note]");
        if (oilMlNote) oilMlNote.textContent = `${oilResult.mlPerDay} mL per day (assuming a 1 mL dropper)`;
        
        const oilConcentration = this.querySelector("[data-result-oil-concentration]");
        if (oilConcentration) oilConcentration.textContent = oilResult.mgPerMl;
        
        const oilVolume = this.querySelector("[data-result-oil-volume]");
        if (oilVolume) oilVolume.textContent = oilResult.mlPerDay;
        
        const advancedArea = this.querySelector("[data-result-oil-advanced]");
        if (advancedArea) {
          if (oilResult.dropsPerDay !== null) {
            advancedArea.classList.remove("hidden");
            const oilDrops = this.querySelector("[data-result-oil-drops]");
            if (oilDrops) oilDrops.textContent = `${oilResult.dropsPerDay} drops`;
          } else {
            advancedArea.classList.add("hidden");
          }
        }
      } else {
        oilResultArea.classList.add("hidden");
      }
    } else if (oilResultArea) {
      oilResultArea.classList.add("hidden");
    }

    // Update Unit Results (Capsule/Gummy)
    const unitResultArea = this.querySelector("[data-result-unit-area]");
    if ((productType === "capsule" || productType === "gummy") && unitResultArea) {
      const mg = parseFloat(mgPerUnit);
      if (!isNaN(mg) && mg > 0) {
        const unitResult = calculateUnitServings(dosage.targetMgPerDay, mg);
        unitResultArea.classList.remove("hidden");
        
        const unitTitle = this.querySelector("[data-result-unit-title]");
        if (unitTitle) unitTitle.textContent = productType === "capsule" ? "Capsule Servings" : "Gummy Servings";
        
        this.querySelector("[data-result-unit-icon-capsule]")?.classList.toggle("hidden", productType !== "capsule");
        this.querySelector("[data-result-unit-icon-gummy]")?.classList.toggle("hidden", productType !== "gummy");
        
        const unitMg = this.querySelector("[data-result-unit-mg]");
        if (unitMg) unitMg.textContent = unitResult.mgPerUnit;
        
        const unitCount = this.querySelector("[data-result-unit-count]");
        if (unitCount) unitCount.textContent = unitResult.unitsPerDay;
        
        const unitSuffix = this.querySelector("[data-result-unit-suffix]");
        if (unitSuffix) {
          unitSuffix.textContent = productType === "capsule" 
            ? (unitResult.unitsPerDay === 1 ? "capsule" : "capsules")
            : (unitResult.unitsPerDay === 1 ? "gummy" : "gummies");
        }
      } else {
        unitResultArea.classList.add("hidden");
      }
    } else if (unitResultArea) {
      unitResultArea.classList.add("hidden");
    }
  }
}

if (!customElements.get("cbd-calculator")) {
  customElements.define("cbd-calculator", CBDCalculator);
}
